import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Card,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Typography,
  Stack,
  Grid,
  CircularProgress,
  Box,
  Autocomplete,
  TextField,
  alpha,
} from "@mui/material";
import { motion } from "framer-motion";
import HomeLayout from "../../asset/layout/HomeLayout";
import { Add } from "@mui/icons-material";
import CardGiftcardOutlinedIcon from "@mui/icons-material/CardGiftcardOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import TrendingDownOutlinedIcon from "@mui/icons-material/TrendingDownOutlined";
import StarsOutlinedIcon from "@mui/icons-material/StarsOutlined";
import FilterListOutlinedIcon from "@mui/icons-material/FilterListOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";

import CustomButton from "../../components/mui/CustomButton";

import { useDispatch, useSelector } from "react-redux";

import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { useNavigate } from "react-router-dom";
import DropDownMenu from "../../components/mui/DropDownMenu";

import { FaEye } from "react-icons/fa";
import SearchBar from "../../components/mui/SearchBar";
import dayjs from "dayjs";
import { getAllRewards, postTheReward } from "../../actions/ticketAuth";

import ViewRewardModal from "../../components/ViewRewardModal";
import RModal from "../../components/mui/RModal";
import InputField1 from "../../components/mui/InputField1";
import { getAllUsers } from "../../actions/userAuth";
import { enqueueSnackbar } from "notistack";
import ResponsiveDatePickers from "../../components/mui/PichDate";
import * as XLSX from "xlsx";
const columns = [
  "Points",
  "From",
  "To",
  "Created on",
  "Action",
  "Remark",
  "Transaction ID",
  "Actions",
];

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.04, duration: 0.45, ease: [0.22, 1, 0.36, 1] },
  }),
};

const PanelCard = ({ children, sx = {} }) => (
  <Card
    elevation={0}
    sx={{
      borderRadius: "24px",
      border: "1px solid",
      borderColor: alpha("#11409B", 0.08),
      bgcolor: "#fff",
      boxShadow: "0 4px 32px rgba(15, 23, 42, 0.06)",
      overflow: "hidden",
      ...sx,
    }}
  >
    {children}
  </Card>
);

const StatCard = ({ icon: Icon, label, value, accent, gradient }) => (
  <PanelCard sx={{ height: "100%" }}>
    <Box sx={{ p: 2.5 }}>
      <Stack direction="row" alignItems="center" spacing={2}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: "14px",
            background: gradient,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: `0 8px 24px ${alpha(accent, 0.35)}`,
          }}
        >
          <Icon sx={{ color: "#fff", fontSize: 24 }} />
        </Box>
        <Box>
          <Typography
            variant="caption"
            sx={{
              color: "#64748b",
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              fontSize: "0.7rem",
            }}
          >
            {label}
          </Typography>
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: "1.75rem",
              color: "#0f172a",
              letterSpacing: "-0.03em",
              lineHeight: 1.1,
            }}
          >
            {value ?? "—"}
          </Typography>
        </Box>
      </Stack>
    </Box>
  </PanelCard>
);

const ActionChip = ({ action }) => {
  const isDebit = String(action || "").toUpperCase() === "DEBIT";
  const color = isDebit ? "#dc2626" : "#16a34a";
  return (
    <Chip
      label={action ? String(action).toLowerCase() : "—"}
      size="small"
      sx={{
        fontWeight: 700,
        fontSize: "0.7rem",
        color,
        bgcolor: alpha(color, 0.12),
        border: `1px solid ${alpha(color, 0.2)}`,
        textTransform: "capitalize",
      }}
    />
  );
};

const normalizeRewardsList = (data) => {
  const raw =
    data?.data?.content ||
    data?.data?.data?.content ||
    data?.data?.data?.transactions ||
    data?.data?.transactions ||
    (Array.isArray(data?.data?.data) ? data.data.data : null) ||
    (Array.isArray(data?.data) ? data.data : null);
  return Array.isArray(raw) ? raw : [];
};

const formatPoints = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n.toFixed(2) : "—";
};

const getPartyLabel = (reward, side) => {
  if (side === "from") {
    return (
      reward?.senderName ||
      reward?.giverName ||
      reward?.from?.username ||
      reward?.from?.phone ||
      reward?.from?.email ||
      reward?.senderUserId ||
      reward?.giverUserId ||
      "—"
    );
  }
  return (
    reward?.receiverName ||
    reward?.to?.username ||
    reward?.to?.phone ||
    reward?.to?.email ||
    reward?.receiverUserId ||
    "—"
  );
};

const matchesRewardSearch = (reward, term) => {
  const q = term?.trim().toLowerCase();
  if (!q) return true;
  const haystack = [
    reward?.senderName,
    reward?.receiverName,
    reward?.senderUserId,
    reward?.receiverUserId,
    reward?.transactionId,
    reward?.walletId,
    reward?.remarks,
    reward?.remark,
    reward?.action,
    reward?.points,
    reward?.point,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return haystack.includes(q);
};

const rewardStatus = ["CREDIT", "DEBIT"];

export const RewardManagement = () => {
  const [open, setOpen] = useState(false);
  const [openSecondModal, setOpenSecondModal] = useState(false);
  const [users, setUsers] = useState([]);
  const [allUserData, setAllUserData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [countPerPage, setCountPerPage] = useState(10);
  const [allBackendUser, setAllBackendUser] = useState(25);
  const [openWarningModal, setOpenWarningModal] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDateFrom, setSelectedDateFrom] = useState(null);
  const [selectedDateTo, setSelectedDateTo] = useState(null);
  const [selectedRewards, setSelectedRewards] = useState(null);
  const { user } = useSelector((state) => ({ ...state }));

  const handleSearch = (term) => {
    setSearchTerm(term);
    setCurrentPage(1);
  };

  const allUser = [];
  console.log("user is", allUser);

  const userId = user.id;
  console.log(userId);
  useEffect(() => {
    if (!localStorage.getItem("token")) {
      Navigate("/");
    }
  }, [user]);

  useEffect(() => {
    console.log("User ID:", user);
  }, [user]);

  console.log(userId);
  const allUserPhoneNumbers = [];

  for (const userId in allUser) {
    const user = allUser[userId];
    if (user && user.phone) {
      allUserPhoneNumbers.push(user.id);
    }
  }

  const dropdownRef = useRef(null);

  const [rewardOption, setRewardOption] = useState("");
  const [rewardOptionSerach, setRewardOptionSerach] = useState("");

  const [filterValue, setFilterValue] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [amountReward, setAmountReward] = useState();
  const [rewardTo, setRewardTo] = useState([]);
  const [remark, setRemark] = useState([]);

  const [totalTransactions, setTotalTransactions] = useState();
  const [pendingTransactions, setPendingTransactions] = useState();
  const [failedTransactions, setFailedTransactions] = useState();
  const [thisWeekTransactions, setThisWeekTransactions] = useState();
  const dispatch = useDispatch();
  const Navigate = useNavigate();
  const roleDropdownRef = useRef(null);
  const handleCloseSecondModal = () => {
    setOpenSecondModal(false);
  };

  const [getReward, setGetReward] = useState([]);

  const getData = () => {
    console.log("Fetching data...");
    setLoading(true);
    dispatch(
      getAllRewards(
        {
          page: 0,
          size: 10000,
          createdFrom: selectedDateFrom
            ? new Date(selectedDateFrom).toISOString()
            : undefined,
          createdTo: selectedDateTo
            ? new Date(selectedDateTo).toISOString()
            : undefined,
        },
        (data) => {
          setGetReward(normalizeRewardsList(data));
          setLoading(false);
        },
        (error) => {
          console.error("Error fetching data:", error);
          setLoading(false);
        }
      )
    );
  };

  const getUser = () => {
    setLoading(true);
    dispatch(
      getAllUsers(
        (data) => {
          console.log("Sorted data:", data.data.data.users);

          setAllUserData(data.data.data.users);

          setLoading(false);
        },
        (error) => {
          console.log("getting error ");
          setLoading(false);
        }
      )
    );
  };

  useEffect(() => {
    getData();
    getUser();
  }, [selectedDateFrom, selectedDateTo]);

  const filteredRewards = useMemo(() => {
    return getReward.filter((reward) => {
      if (!matchesRewardSearch(reward, searchTerm)) return false;
      if (selectedDateFrom) {
        const from = new Date(selectedDateFrom);
        if (new Date(reward.createdAt) < from) return false;
      }
      if (selectedDateTo) {
        const to = new Date(selectedDateTo);
        to.setHours(23, 59, 59, 999);
        if (new Date(reward.createdAt) > to) return false;
      }
      return true;
    });
  }, [getReward, searchTerm, selectedDateFrom, selectedDateTo]);
  const autoCompleteOptions = allUserData.map((user) => {
    if (user.username && user.username !== "") {
      return {
        label: `${user.username} (${user.phone || "No phone"})`,
        value: user.username,
      };
    } else if (user.email && user.email !== "") {
      return {
        label: `${user.email} (${user.phone || "No phone"})`,
        value: user.email,
      };
    } else if (user.phone && user.phone !== "") {
      return { label: user.phone, value: user.phone };
    } else {
      return { label: "Unknown", value: "Unknown" };
    }
  });

  const formDataAdd = {
    receiverUserId: Array.isArray(rewardTo) ? rewardTo[0] : rewardTo,
    points: Number(amountReward),
    action: rewardOption || "CREDIT",
    remarks: remark,
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log("submit modal");

    setOpenWarningModal(true);
  };

  console.log("autoCompleteOptions is", autoCompleteOptions);

  const handleOpenSecondModal = () => {
    setOpenSecondModal(true);
  };

  const handleCloseWarningModal = () => {
    setOpenWarningModal(false);
  };
  const handleWarningModalOpen = () => {
    setOpenWarningModal(false);
  };
  const [viewOpen, setViewOpen] = useState(false);

  const handleOpenModalView = (reward) => {
    console.log("hello i am here");
    setSelectedRewards(reward);
    setViewOpen(true);
  };
  const handleCloseModalVIew = () => {
    setViewOpen(false);
    setSelectedRewards(null);
  };

  const handleReset = () => {
    setAmountReward("");
    setRewardTo([]);
    setRewardOption("");
    setFilterValue("");
    setSearchQuery("");
    setRemark("");
  };

  const handleSubmitWarningModal = (e) => {
    e.preventDefault();
    if (!formDataAdd.receiverUserId) {
      enqueueSnackbar("Please select a receiver user", { variant: "error" });
      return;
    }
    if (!Number.isFinite(formDataAdd.points) || formDataAdd.points <= 0) {
      enqueueSnackbar("Please enter valid points", { variant: "error" });
      return;
    }
    if (!formDataAdd.action) {
      enqueueSnackbar("Please select reward action", { variant: "error" });
      return;
    }
    dispatch(
      postTheReward(
        formDataAdd,
        (response) => {
          enqueueSnackbar("Data submitted successfully", {
            variant: "success",
          });

          setLoading(false);
          handleCloseSecondModal();
          handleCloseWarningModal();
          getData();
        },
        (error) => {
          enqueueSnackbar(error?.response?.data?.message, {
            variant: "error",
          });
        }
      )
    );
  };

  const handleCloseAllModal = () => {
    handleCloseWarningModal();
    handleCloseSecondModal();
  };
  const handleSearchClear = () => {
    setSearchTerm("");
    setSelectedDateFrom(null);
    setSelectedDateTo(null);
    setSearchQuery("");
    setCurrentPage(1);
  };

  const handleExport = () => {
    const workbook = XLSX.utils.book_new();
    const worksheetData = [
      [
        "Points",
        "Sender",
        "Receiver",
        "Action",
        "Remarks",
        "Transaction ID",
        "Wallet ID",
        "Created on",
      ],
      ...filteredRewards.map((row) => [
        formatPoints(row.points ?? row.point),
        getPartyLabel(row, "from"),
        getPartyLabel(row, "to"),
        row.action || "—",
        row.remarks || row.remark || "—",
        row.transactionId || "—",
        row.walletId || "—",
        row.createdAt
          ? dayjs(row.createdAt).format("DD MMM YYYY HH:mm")
          : "—",
      ]),
    ];
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    XLSX.utils.book_append_sheet(workbook, worksheet, "Rewards");
    XLSX.writeFile(workbook, "rewards_data.xlsx");
  };

  const paginatedData = filteredRewards.slice(
    (currentPage - 1) * countPerPage,
    currentPage * countPerPage
  );

  const totalPages = Math.ceil(filteredRewards.length / countPerPage) || 1;

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(filteredRewards.length / countPerPage) || 1);
    if (currentPage > maxPage) setCurrentPage(maxPage);
  }, [filteredRewards.length, countPerPage, currentPage]);

  const stats = useMemo(() => {
    const total = filteredRewards.length;
    const credit = filteredRewards.filter(
      (r) => String(r.action || "").toUpperCase() === "CREDIT"
    ).length;
    const debit = filteredRewards.filter(
      (r) => String(r.action || "").toUpperCase() === "DEBIT"
    ).length;
    const totalPoints = filteredRewards.reduce(
      (sum, r) => sum + (Number(r.points ?? r.point) || 0),
      0
    );
    return { total, credit, debit, totalPoints };
  }, [filteredRewards]);

  const premiumButtonSx = {
    height: "44px",
    borderRadius: "12px",
    fontWeight: 600,
    textTransform: "none",
    background: "linear-gradient(135deg, #11409B 0%, #2563eb 100%)",
    boxShadow: "0 6px 24px rgba(37, 99, 235, 0.35)",
    "&:hover": {
      background: "linear-gradient(135deg, #0f3580 0%, #1d4ed8 100%)",
    },
  };

  return (
    <HomeLayout>
      <Box
        sx={{
          minHeight: "100%",
          background:
            "linear-gradient(180deg, #f0f4fa 0%, #f5f7fa 35%, #eef2f7 100%)",
          mx: -3,
          mt: -3,
          px: 3,
          pt: 3,
          pb: 4,
        }}
      >
        <motion.div initial="hidden" animate="visible" variants={fadeUp}>
          <Box
            sx={{
              p: { xs: 3, md: 4 },
              mb: 3,
              borderRadius: "24px",
              background:
                "linear-gradient(135deg, #0c3483 0%, #11409B 40%, #1e40af 70%, #2563eb 100%)",
              color: "#fff",
              position: "relative",
              overflow: "hidden",
              boxShadow: "0 20px 60px rgba(17, 64, 155, 0.35)",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: -50,
                right: -30,
                width: 200,
                height: 200,
                borderRadius: "50%",
                bgcolor: alpha("#fff", 0.06),
              }}
            />
            <Stack
              direction={{ xs: "column", sm: "row" }}
              alignItems={{ xs: "flex-start", sm: "center" }}
              justifyContent="space-between"
              spacing={2}
              sx={{ position: "relative", zIndex: 1 }}
            >
              <Box>
                <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                  <CardGiftcardOutlinedIcon />
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 600, letterSpacing: "0.1em", opacity: 0.85 }}
                  >
                    REWARDS
                  </Typography>
                </Stack>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 800, letterSpacing: "-0.03em" }}
                >
                  Reward management
                </Typography>
                <Typography sx={{ mt: 1, opacity: 0.82, maxWidth: 520 }}>
                  Credit or revoke reward points, review transaction history, and
                  export records for your users.
                </Typography>
              </Box>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                <CustomButton
                  variant="contained"
                  onClick={handleExport}
                  startIcon={<FileDownloadOutlinedIcon />}
                  sx={{
                    ...premiumButtonSx,
                    bgcolor: alpha("#fff", 0.15),
                    backdropFilter: "blur(8px)",
                    border: `1px solid ${alpha("#fff", 0.25)}`,
                    boxShadow: "none",
                    "&:hover": { bgcolor: alpha("#fff", 0.22) },
                  }}
                >
                  Export rewards
                </CustomButton>
                <CustomButton
                  variant="contained"
                  onClick={handleOpenSecondModal}
                  startIcon={<Add />}
                  sx={{
                    ...premiumButtonSx,
                    bgcolor: alpha("#fff", 0.15),
                    backdropFilter: "blur(8px)",
                    border: `1px solid ${alpha("#fff", 0.25)}`,
                    boxShadow: "none",
                    "&:hover": { bgcolor: alpha("#fff", 0.22) },
                  }}
                >
                  Manage reward
                </CustomButton>
              </Stack>
            </Stack>
          </Box>
        </motion.div>

        <Grid container spacing={2.5} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <motion.div custom={0} initial="hidden" animate="visible" variants={fadeUp}>
              <StatCard
                icon={CardGiftcardOutlinedIcon}
                label="Total records"
                value={stats.total}
                accent="#4f46e5"
                gradient="linear-gradient(135deg, #818cf8 0%, #4f46e5 100%)"
              />
            </motion.div>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <motion.div custom={1} initial="hidden" animate="visible" variants={fadeUp}>
              <StatCard
                icon={TrendingUpOutlinedIcon}
                label="Credits"
                value={stats.credit}
                accent="#059669"
                gradient="linear-gradient(135deg, #34d399 0%, #059669 100%)"
              />
            </motion.div>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <motion.div custom={2} initial="hidden" animate="visible" variants={fadeUp}>
              <StatCard
                icon={TrendingDownOutlinedIcon}
                label="Debits"
                value={stats.debit}
                accent="#dc2626"
                gradient="linear-gradient(135deg, #f87171 0%, #dc2626 100%)"
              />
            </motion.div>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <motion.div custom={3} initial="hidden" animate="visible" variants={fadeUp}>
              <StatCard
                icon={StarsOutlinedIcon}
                label="Points moved"
                value={stats.totalPoints.toLocaleString("en-IN")}
                accent="#7c3aed"
                gradient="linear-gradient(135deg, #a78bfa 0%, #7c3aed 100%)"
              />
            </motion.div>
          </Grid>
        </Grid>

        <motion.div custom={4} initial="hidden" animate="visible" variants={fadeUp}>
          <PanelCard sx={{ mb: 3, p: { xs: 2, md: 2.5 } }}>
            <Stack direction="row" alignItems="center" spacing={1} mb={2}>
              <FilterListOutlinedIcon sx={{ color: "#11409B", fontSize: 20 }} />
              <Typography sx={{ fontWeight: 700, color: "#0f172a" }}>
                Filters
              </Typography>
            </Stack>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <SearchBar
                  onSearch={handleSearch}
                  searchTerm={searchTerm}
                  height="2.75rem"
                  placeholder="Search sender, receiver, transaction ID…"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <ResponsiveDatePickers
                  selectedDate={selectedDateFrom}
                  setSelectedDate={setSelectedDateFrom}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <ResponsiveDatePickers
                  selectedDate={selectedDateTo}
                  setSelectedDate={setSelectedDateTo}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <CustomButton
                  variant="outlined"
                  onClick={handleSearchClear}
                  fullWidth
                  sx={{
                    height: "2.75rem",
                    borderRadius: "12px",
                    textTransform: "none",
                    fontWeight: 600,
                    borderColor: alpha("#11409B", 0.25),
                    color: "#11409B",
                    "&:hover": {
                      borderColor: "#11409B",
                      bgcolor: alpha("#11409B", 0.04),
                    },
                  }}
                >
                  Clear
                </CustomButton>
              </Grid>
            </Grid>
          </PanelCard>
        </motion.div>

        <motion.div custom={5} initial="hidden" animate="visible" variants={fadeUp}>
          {loading ? (
            <PanelCard>
              <Stack alignItems="center" justifyContent="center" sx={{ py: 10 }}>
                <CircularProgress sx={{ color: "#11409B" }} />
                <Typography sx={{ mt: 2, color: "#64748b" }}>
                  Loading rewards…
                </Typography>
              </Stack>
            </PanelCard>
          ) : paginatedData?.length > 0 ? (
            <TableContainer component={PanelCard} sx={{ overflowX: "auto" }}>
              <Table>
                <TableHead>
                  <TableRow
                    sx={{
                      bgcolor: alpha("#11409B", 0.04),
                      "& th": {
                        borderBottom: `1px solid ${alpha("#11409B", 0.08)}`,
                      },
                    }}
                  >
                    {columns.map((column) => (
                      <TableCell
                        key={column}
                        sx={{
                          fontSize: "0.75rem",
                          fontWeight: 700,
                          color: "#11409B",
                          textTransform: "uppercase",
                          letterSpacing: "0.06em",
                          py: 2,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {column}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedData.map((reward) => (
                    <TableRow
                      key={reward?.id || reward?._id}
                      sx={{
                        transition: "background 0.2s",
                        "&:hover": { bgcolor: alpha("#11409B", 0.03) },
                        "& td": {
                          borderBottom: `1px solid ${alpha("#11409B", 0.06)}`,
                          py: 1.75,
                        },
                      }}
                    >
                      <TableCell>
                        <Typography sx={{ fontWeight: 800, color: "#0d9488" }}>
                          {formatPoints(reward?.points ?? reward?.point)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: "#0f172a" }}>
                          {getPartyLabel(reward, "from")}
                        </Typography>
                        {reward?.senderUserId && (
                          <Typography
                            variant="caption"
                            sx={{ color: "#94a3b8", fontFamily: "monospace", display: "block" }}
                          >
                            {reward.senderUserId}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: "#0f172a" }}>
                          {getPartyLabel(reward, "to")}
                        </Typography>
                        {reward?.receiverUserId && (
                          <Typography
                            variant="caption"
                            sx={{ color: "#94a3b8", fontFamily: "monospace", display: "block" }}
                          >
                            {reward.receiverUserId}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: "#64748b" }}>
                          {reward?.createdAt
                            ? dayjs(reward.createdAt).format("DD MMM YYYY, HH:mm")
                            : "—"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <ActionChip action={reward?.action} />
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{ color: "#64748b", maxWidth: 180, whiteSpace: "normal" }}
                        >
                          {reward?.remarks || reward?.remark || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "#64748b",
                            fontFamily: "monospace",
                            wordBreak: "break-all",
                            maxWidth: 140,
                            display: "block",
                          }}
                        >
                          {reward?.transactionId || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <IconButton
                          onClick={() => handleOpenModalView(reward)}
                          size="small"
                          sx={{
                            borderRadius: "10px",
                            border: `1px solid ${alpha("#11409B", 0.15)}`,
                            color: "#11409B",
                            "&:hover": { bgcolor: alpha("#11409B", 0.06) },
                          }}
                        >
                          <FaEye />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <PanelCard>
              <Stack alignItems="center" sx={{ py: 8, px: 3 }}>
                <CardGiftcardOutlinedIcon
                  sx={{ fontSize: 56, color: "#cbd5e1", mb: 2 }}
                />
                <Typography variant="h6" sx={{ fontWeight: 700, color: "#0f172a" }}>
                  No rewards found
                </Typography>
                <Typography sx={{ color: "#64748b", mt: 1, textAlign: "center" }}>
                  Try adjusting your search or date filters, or manage points for a user.
                </Typography>
                <CustomButton
                  variant="contained"
                  onClick={handleOpenSecondModal}
                  startIcon={<Add />}
                  sx={{ ...premiumButtonSx, mt: 3 }}
                >
                  Manage reward
                </CustomButton>
              </Stack>
            </PanelCard>
          )}
        </motion.div>

        {!loading && filteredRewards.length > 0 && (
          <motion.div custom={6} initial="hidden" animate="visible" variants={fadeUp}>
            <PanelCard sx={{ mt: 3, p: 2 }}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                alignItems="center"
                justifyContent="space-between"
                spacing={2}
              >
                <Typography sx={{ color: "#64748b", fontWeight: 500 }}>
                  Showing {(currentPage - 1) * countPerPage + 1}–
                  {Math.min(currentPage * countPerPage, filteredRewards.length)} of{" "}
                  {filteredRewards.length}
                </Typography>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Typography variant="body2" sx={{ color: "#64748b" }}>
                    Rows per page
                  </Typography>
                  <Box
                    component="select"
                    value={countPerPage}
                    onChange={(e) => {
                      setCountPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    sx={{
                      border: `1px solid ${alpha("#11409B", 0.2)}`,
                      borderRadius: "10px",
                      px: 1.5,
                      py: 0.75,
                      fontSize: "0.875rem",
                      color: "#0f172a",
                      bgcolor: "#fff",
                      outline: "none",
                    }}
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={30}>30</option>
                  </Box>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <IconButton
                      onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                      disabled={currentPage === 1}
                      sx={{
                        border: "1px solid",
                        borderColor: alpha("#11409B", 0.15),
                        borderRadius: "12px",
                        bgcolor: "#fff",
                        "&:hover": { bgcolor: alpha("#11409B", 0.06) },
                      }}
                    >
                      <KeyboardArrowLeftIcon />
                    </IconButton>
                    <Typography sx={{ fontWeight: 600, minWidth: 48, textAlign: "center" }}>
                      {currentPage} / {totalPages}
                    </Typography>
                    <IconButton
                      onClick={() =>
                        setCurrentPage((p) => Math.min(p + 1, totalPages))
                      }
                      disabled={currentPage >= totalPages}
                      sx={{
                        border: "1px solid",
                        borderColor: alpha("#11409B", 0.15),
                        borderRadius: "12px",
                        bgcolor: "#fff",
                        "&:hover": { bgcolor: alpha("#11409B", 0.06) },
                      }}
                    >
                      <KeyboardArrowRightIcon />
                    </IconButton>
                  </Stack>
                </Stack>
              </Stack>
            </PanelCard>
          </motion.div>
        )}
      </Box>
      {viewOpen && (
        <ViewRewardModal
          open={viewOpen}
          handleClose={handleCloseModalVIew}
          reward={selectedRewards}
        />
      )}
      {openSecondModal && (
        <RModal
          open={openSecondModal}
          handleClose={handleCloseSecondModal}
          width={{ md: "60%", xs: "80%" }}
          height={"auto"}
        >
          <form onSubmit={handleSubmit}>
            <Typography variant="h4">Manage Points</Typography>
            <Grid container spacing={{ md: 1, sm: 0, xs: 0 }}>
              <Grid item md={6} xs={12} sm={12}>
                <Typography variant="h5" my={2}>
                  Action
                </Typography>
                <DropDownMenu
                  buttonText="Reward action"
                  menuItems={rewardStatus?.map((item) => {
                    return { label: item };
                  })}
                  selectedItem={rewardOption}
                  setSelectedItem={setRewardOption}
                  buttonWidth={"100%"}
                />
              </Grid>
              <Grid item md={6} xs={12} sm={12}>
                <InputField1
                  placeholderColor=" #8D98AA"
                  label={"amount"}
                  placeholder={"Enter amount"}
                  spanP={true}
                  value={amountReward}
                  onChange={(e) => setAmountReward(e?.target?.value)}
                  required
                  type={"number"}
                  maxLength={20}
                />
              </Grid>

              <Grid item md={6} xs={12}>
                <Typography variant="h5" my={1.5}>
                  To
                </Typography>

                <Autocomplete
                  id="tags-filled"
                  options={autoCompleteOptions}
                  value={
                    autoCompleteOptions.find((option) => {
                      const user = allUserData.find(
                        (u) =>
                          u.username === option.value ||
                          u.phone === option.value ||
                          u.email === option.value
                      );
                      return user?.id === rewardTo?.[0];
                    }) || null
                  }
                  getOptionLabel={(option) => option.label}
                  onChange={(event, newValue) => {
                    if (!newValue) {
                      setRewardTo([]);
                      return;
                    }
                    const selectedUser = allUserData.find(
                      (u) =>
                        u.username === newValue.value ||
                        u.phone === newValue.value ||
                        u.email === newValue.value
                    );
                    setRewardTo(selectedUser?.id ? [selectedUser.id] : []);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Assigned To"
                      sx={{
                        "& .MuiInputBase-root": {
                          height: "2.5rem",
                          background: "#FFFFFF",
                          borderRadius: "7px",
                          paddingRight: "8px",
                          py: "auto",
                        },
                        "& .MuiInputBase-input": {
                          mt: "-12px",
                        },
                        "& .MuiAutocomplete-tag": {
                          mt: "-7px !important",
                        },
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item md={6} xs={12} sm={12}>
                <InputField1
                  placeholderColor=" #8D98AA"
                  label={"Remark"}
                  placeholder={"Enter Remark"}
                  spanP={true}
                  value={remark}
                  onChange={(e) => setRemark(e?.target?.value)}
                  required
                />
              </Grid>
            </Grid>

            <Stack
              direction={"row"}
              spacing={{ xl: 6, md: 6, sm: 1, xs: 1 }}
              mt={4}
            >
              {" "}
              <CustomButton
                variant="contained"
                width={{ md: "20%", sm: "50%", xs: "60%" }}
                height={"3rem"}
                type="submit"
              >
                {loading ? (
                  <CircularProgress size={24} />
                ) : rewardOption === "DEBIT" ? (
                  "Revoke Points"
                ) : (
                  "Add Points"
                )}
              </CustomButton>
              <CustomButton
                variant="outlined"
                width={{ md: "20%", sm: "50%", xs: "40%" }}
                height={"3rem"}
                type="button"
                onClick={handleReset}
              >
                Reset
              </CustomButton>
            </Stack>
          </form>
        </RModal>
      )}

      {openWarningModal && (
        <RModal
          open={openWarningModal}
          handleClose={handleCloseWarningModal}
          width={"40%"}
          height={"auto"}
        >
          <Typography variant="h4"> Are you sure</Typography>
          <Typography mt={3} variant="h5">
            {" "}
            This action can not be reverted once done, are you sure ?
          </Typography>
          <Stack
            direction={"row"}
            spacing={{ xl: 6, md: 6, sm: 1, xs: 1 }}
            mt={4}
          >
            {" "}
            <CustomButton
              variant="contained"
              width={{ md: "20%", sm: "50%", xs: "60%" }}
              height={"3rem"}
              onClick={handleSubmitWarningModal}
            >
              Yes
            </CustomButton>
            <CustomButton
              variant="outlined"
              width={{ md: "20%", sm: "50%", xs: "40%" }}
              height={"3rem"}
              type="button"
              onClick={handleCloseAllModal}
            >
              No
            </CustomButton>
          </Stack>
        </RModal>
      )}
    </HomeLayout>
  );
};
